import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";

const SITE_URL = "https://it-servicepoint.eu";
const DEFAULT_OG = `${SITE_URL}/brand/logo-full.jpg`;

export const SEO = ({
  title,
  description,
  keywords,
  image = DEFAULT_OG,
  type = "website",
  jsonLd,
  noindex = false,
}) => {
  const { lang } = useI18n();
  const location = useLocation();
  const canonical = `${SITE_URL}${location.pathname}`;
  const altLang = lang === "de" ? "en" : "de";

  const fullTitle = title
    ? `${title} | IT-Servicepoint`
    : "IT-Servicepoint · IT-Dienstleister, IT-Service & IT-Beratung · Made in Germany";
  const desc =
    description ||
    "IT-Servicepoint – Ihr IT-Dienstleister in Hessen: IT-Service, Managed Security, Backup, Server Hosting, IT-Beratung und Schwachstellenmanagement für den Mittelstand. Über 10 Jahre Expertise, made in Germany.";
  const kw =
    keywords ||
    "IT-Dienstleister, IT Service, IT Berater, IT-Beratung, IT-Sicherheit, Managed Security, IT-Service Hessen, IT-Dienstleister Fulda, Schwachstellenmanagement, Managed Backup, Server Hosting, Cybersecurity Mittelstand, Securepoint, Firewall, DSGVO, NIS-2";

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="author" content="IT-Servicepoint · Ron Kowalewsky" />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow,max-snippet:-1,max-image-preview:large"} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang={lang} href={canonical} />
      <link rel="alternate" hrefLang={altLang} href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${location.pathname}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={lang === "de" ? "de_DE" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "de" ? "en_US" : "de_DE"} />
      <meta property="og:site_name" content="IT-Servicepoint" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content="IT-Servicepoint Logo" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* Theme color matching logo */}
      <meta name="theme-color" content="#1A4D6E" />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": `${SITE_URL}/#organization`,
  name: "IT-Servicepoint",
  legalName: "IT Servicepoint · Ron Kowalewsky",
  alternateName: ["IT Servicepoint", "IT-Dienstleister Hessen", "IT Service Bad Hersfeld"],
  slogan: "IT-Sicherheit mit Sachverstand. Made in Germany.",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/logo-full.jpg`,
  image: `${SITE_URL}/brand/logo-full.jpg`,
  description:
    "Inhabergeführter IT-Dienstleister in Hessen: IT-Service, IT-Beratung, Managed Security, Managed Backup, Server Hosting, Schwachstellenmanagement und Managed WLAN für den deutschen Mittelstand. Made in Germany.",
  email: "info@it-servicepoint.eu",
  telephone: "+49-6629-9153890",
  founder: { "@type": "Person", name: "Ron Kowalewsky" },
  foundingDate: "2014",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Im Wiesengrund 2A",
    postalCode: "36277",
    addressLocality: "Schenklengsfeld",
    addressRegion: "Hessen",
    addressCountry: "DE",
  },
  geo: { "@type": "GeoCoordinates", latitude: 50.831, longitude: 9.866 },
  areaServed: ["DE", "AT", "CH"],
  priceRange: "€€",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    },
  ],
  sameAs: [
    "https://www.google.com/maps/place/IT-Servicepoint",
    "https://it-servicepoint.eu",
  ],
  knowsAbout: [
    "IT-Dienstleister", "IT-Service", "IT-Beratung", "IT-Support",
    "Managed Security", "IT-Sicherheit", "Schwachstellenmanagement",
    "Managed Backup", "Server Hosting", "Managed WLAN", "Webdesign",
    "SEO Fulda", "NIS-2 Compliance", "Ransomware Schutz", "Videoüberwachung",
    "Lokale KI", "On-Premise KI", "DSGVO-konforme KI", "KI für Anwaltskanzleien",
    "KI für Steuerberater", "Künstliche Intelligenz im Unternehmen",
  ],
  award: ["Securepoint Partner", "Greenbone Certified"],
  memberOf: [
    { "@type": "Organization", name: "IHK Fulda" },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "IT-Services",
    itemListElement: [
      "Lokale KI (On-Premise, DSGVO-konform)",
      "Managed Security",
      "Managed Backup & Recovery",
      "Managed Server Hosting",
      "Schwachstellenmanagement",
      "Managed WLAN",
    ].map((n, i) => ({
      "@type": "Offer",
      position: i + 1,
      itemOffered: { "@type": "Service", name: n, provider: { "@id": `${SITE_URL}/#organization` } },
    })),
  },
};

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const serviceJsonLd = (name, description) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: "DE",
  serviceType: name,
});

export const articleJsonLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title_de,
  description: post.excerpt_de,
  image: post.cover_image,
  datePublished: post.created_at,
  dateModified: post.updated_at,
  author: { "@type": "Organization", name: "IT-Servicepoint" },
  publisher: { "@id": `${SITE_URL}/#organization` },
  mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
});
